// Step ids matching an INSTALLATION_STEP choice get persisted on `Installation.step`;
// the others are UI-only and don't survive a refresh.
export const WIZARD_STEPS = [
  { id: "start", label: "CRM Information", sub: "Client, email, cameras", branches: ["cloud", "local"] },
  { id: "scan", label: "Installation Information", sub: "Network & type", branches: ["cloud", "local"] },
  { id: "dvr", label: "Video Recorder", sub: "Login, ports", branches: ["cloud", "local"] },
  { id: "preparation", label: "Asset Selection", sub: "NUC server assignment", branches: ["local"] },
  { id: "logistics", label: "Shipping Status", sub: "Carrier & tracking", branches: ["local"] },
  { id: "router", label: "Router Information", sub: "Brand, login, password", branches: ["cloud"] },
  { id: "port_forwarding", label: "Port Forwarding", sub: "Rules & check", branches: ["cloud"] },
  { id: "ddns", label: "DDNS", sub: "URL, credentials, test", branches: ["cloud"] },
  { id: "rtsp_config", label: "RTSP Configuration", sub: "Suffix & check", branches: ["cloud", "local"] },
  {
    id: "channel_selection",
    label: "Channel Selection & Apply",
    sub: "Pick channels, review, apply",
    branches: ["cloud", "local"],
  },
  {
    id: "waiting_for_veesion",
    label: "Quality Review",
    sub: "Veesion QC validation",
    branches: ["cloud", "local"],
  },
  { id: "user_creation", label: "User Creation", sub: "Create client in AppTune", branches: ["cloud", "local"] },
  { id: "completed", label: "Done", sub: "Installation finalized", branches: ["cloud", "local"] },
];

const HEAD = ["start", "scan", "dvr"];
const TAIL = ["rtsp_config", "channel_selection", "waiting_for_veesion", "user_creation", "completed"];
const INSTALLATION_STEP_SEQUENCE = {
  cloud: [...HEAD, "router", "port_forwarding", "ddns", ...TAIL],
  local: [...HEAD, "preparation", "logistics", "delivered", ...TAIL],
};

// Returns a short reason string when the user shouldn't advance from `stepId`,
// or null otherwise.
export function getAdvanceBlocker(stepId, installation, branch, dvrs) {
  if (stepId === "scan" && branch === "cloud") {
    // upload_speed may be 0 (measured but no bandwidth), which is still "set".
    if (!installation?.wan_ipv4 || installation?.upload_speed == null) {
      return "Waiting for Pitlane scan to populate WAN IP and bandwidth.";
    }
  }
  if (stepId === "dvr") {
    if (!(dvrs ?? []).some((d) => d.id != null)) {
      return "Add at least one DVR before continuing.";
    }
  }
  if (branch === "local" && stepId === "logistics") {
    const seq = INSTALLATION_STEP_SEQUENCE.local;
    const currentIdx = seq.indexOf(installation?.step);
    if (currentIdx < seq.indexOf("delivered")) {
      return "Waiting for the carrier to confirm delivery.";
    }
  }
  if (stepId === "channel_selection") {
    // The apply endpoint advances the persisted step to `waiting_for_veesion`
    // once the operator clicks Apply. Until then Next stays disabled.
    const seq = INSTALLATION_STEP_SEQUENCE[branch] ?? [];
    const currentIdx = seq.indexOf(installation?.step);
    if (currentIdx < seq.indexOf("waiting_for_veesion")) {
      return "Click Apply Configuration to save the channel selection.";
    }
  }
  return null;
}

// Fail-closed on either side: an unknown `currentStep` (e.g. cross-branch
// after an `is_cloud` toggle) returns false, so the PATCH gate refuses to
// downgrade saved progress rather than guessing.
export function isAfterInSequence(nextStepId, currentStep, branch) {
  const stepSeq = INSTALLATION_STEP_SEQUENCE[branch] ?? [];
  const currentIdx = stepSeq.indexOf(currentStep);
  const nextIdx = stepSeq.indexOf(nextStepId);
  if (currentIdx === -1 || nextIdx === -1) return false;
  return nextIdx > currentIdx;
}

export function getVisibleSteps(branch) {
  return WIZARD_STEPS.filter((s) => s.branches.includes(branch));
}

export function resolveBranch(installation) {
  return installation?.is_cloud === false ? "local" : "cloud";
}

// Nearest visible predecessor of `missingStepId` in `WIZARD_STEPS` order.
export function findFallbackStep(missingStepId, visibleSteps) {
  if (visibleSteps.length === 0) return undefined;
  const fullIdx = WIZARD_STEPS.findIndex((s) => s.id === missingStepId);
  if (fullIdx === -1) return visibleSteps[0];
  for (let i = fullIdx - 1; i >= 0; i--) {
    const candidate = visibleSteps.find((s) => s.id === WIZARD_STEPS[i].id);
    if (candidate) return candidate;
  }
  return visibleSteps[0];
}

// Latest visible step at-or-before `installation.step` in the branch sequence,
// or the first visible step when nothing's been persisted yet or the saved
// step is cross-branch (PATCH callers must guard against re-anchoring there).
export function resolvePersistedStepId(installation, visibleSteps, branch) {
  if (visibleSteps.length === 0) return undefined;
  const stepSeq = INSTALLATION_STEP_SEQUENCE[branch] ?? [];
  const installationStep = installation?.step ?? "start";
  const installationIdx = stepSeq.indexOf(installationStep);
  if (installationIdx === -1) return visibleSteps[0].id;
  const lastMatch = visibleSteps
    .filter((s) => {
      const mappedIdx = stepSeq.indexOf(s.id);
      return mappedIdx !== -1 && mappedIdx <= installationIdx;
    })
    .at(-1);
  return lastMatch?.id ?? visibleSteps[0].id;
}
